import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { signInWithPhonePassword } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { USE_MOCK } from '@/config'
import { getMockPanelDemoLoginRows } from '@/utils/demo-panel-logins'
import { formatPhoneDisplay, formatPhoneInput } from '@/utils/phone'
import { warmUpAfterLogin } from '@/utils/prefetch'

const LOGIN_DEMO_HINT = import.meta.env.VITE_LOGIN_DEMO_HINT as string | undefined

function LoginDemoHint() {
  const { t } = useTranslation()
  if (USE_MOCK) {
    const rows = getMockPanelDemoLoginRows()
    return (
      <div className="mt-6 space-y-1 text-center text-xs text-[var(--stryzh-color-text-secondary)]">
        <p>{t('login.demoHint')}</p>
        {rows.map((r) => (
          <p key={r.phone}>
            {r.label}: {formatPhoneDisplay(r.phone)}
          </p>
        ))}
      </div>
    )
  }
  const hint = LOGIN_DEMO_HINT?.trim()
  if (hint) {
    const lines = hint.split(/\r?\n/).filter(Boolean)
    return (
      <div className="mt-6 space-y-1 text-center text-xs text-[var(--stryzh-color-text-secondary)]">
        {lines.map((line, i) => (
          <p key={`${i}-${line}`}>{line}</p>
        ))}
      </div>
    )
  }
  return null
}

function normalizePhone(raw: string): string {
  const d = raw.replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('7')) return `+${d}`
  if (d.length === 10) return `+7${d}`
  return raw.startsWith('+') ? raw : `+${d}`
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const loc = useLocation()
  const qc = useQueryClient()
  const from = (loc.state as { from?: Location })?.from?.pathname ?? '/'
  const profile = useAuthStore((s) => s.profile)
  const setProfile = useAuthStore((s) => s.setProfile)
  // Дефолт — телефон seed-админа (`scripts/reset-supabase.mjs`: ADMIN_PHONE),
  // чтобы после полного wipe можно было войти не вбивая номер вручную.
  const [phone, setPhone] = useState(() => formatPhoneDisplay('+77777777777'))
  const [password, setPassword] = useState('demo')
  const [error, setError] = useState('')
  const [showPw, setShowPw] = useState(false)
  // Лоадер на кнопке «Войти»: без него страница после клика «висит» —
  // сетевой round-trip к Supabase Auth + загрузка профиля занимают заметное
  // время, и пользователь жмёт повторно.
  const [submitting, setSubmitting] = useState(false)

  if (profile) {
    return <Navigate to={from} replace />
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError('')
    setSubmitting(true)
    try {
      const p = await signInWithPhonePassword(normalizePhone(phone), password)
      setProfile(p)
      // Прогрев основных списков параллельно с первым рендером главного
      // экрана: пока пользователь смотрит на дашборд, фоном уже летят
      // запросы на /users, /companies и справочники.
      warmUpAfterLogin(qc)
      navigate(from, {
        replace: true,
      })
      // Не сбрасываем `submitting` в успешной ветке: компонент сейчас уйдёт
      // с экрана через Navigate, а пока — кнопка остаётся в состоянии
      // загрузки, чтобы пользователь не успел дождаться сброса и нажать ещё раз.
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.error'))
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[color:var(--stryzh-color-void)] p-4">
      <div className="stryzh-panel w-full max-w-[400px] rounded-[var(--stryzh-radius-lg)] p-[var(--stryzh-space-6)]">
        <div className="mb-[var(--stryzh-space-6)] flex justify-center">
          <img src="/carlink.png" alt="Carlink" className="h-[32px] w-auto" />
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <Input
            label={t('auth.phone')}
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(phone, e.target.value))}
            autoComplete="tel"
            disabled={submitting}
          />
          <div>
            <Input
              label={t('auth.password')}
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
            <button
              type="button"
              className="mt-1 text-xs text-[var(--stryzh-color-text-secondary)] hover:underline disabled:opacity-50"
              onClick={() => setShowPw(!showPw)}
              disabled={submitting}
            >
              {showPw ? t('login.hidePassword') : t('login.showPassword')}
            </button>
          </div>
          {error ? (
            <p className="text-sm text-[color:var(--stryzh-color-semantic-error)]">
              {error}
            </p>
          ) : null}
          <div className="flex justify-center pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[var(--stryzh-color-border-default)] border-t-[var(--stryzh-color-accent-orange)]"
                  />
                  {t('auth.loggingIn')}
                </span>
              ) : (
                t('auth.login')
              )}
            </Button>
          </div>
        </form>
        <LoginDemoHint />
      </div>
    </div>
  )
}
